import { isHoldActive, type BundleInventoryItem } from "@internal/backstage-plugin-avd-common";

export type HealthLevel = "healthy" | "attention" | "failed";

/** One thing an operator may need to decide about; `issue` is the headline, `why` the explanation. */
export type HealthReason = {
	id: string;
	/** Short, for a table tag: "Drifted". */
	label: string;
	issue: string;
	why: string;
	/** `info` is worth showing but not acting on (in progress, or nothing left to manage): it never makes a bundle need attention. */
	level: Exclude<HealthLevel, "healthy"> | "info";
};

export type BundleHealth = { level: HealthLevel; reasons: HealthReason[] };

const LEVEL_RANK: Record<HealthReason["level"] | "healthy", number> = {
	failed: 0,
	attention: 1,
	info: 2,
	healthy: 3,
};

const dayMonth = (iso: string): string =>
	new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });

/** The reasons every bundle type shares: registry drift and the inactivity lifecycle. */
function genericReasons(
	item: BundleInventoryItem,
	now: Date,
	hasNoBundle: boolean,
): HealthReason[] {
	const reasons: HealthReason[] = [];
	// A drift verdict about a bundle that no longer exists is stale; teardown never clears it.
	if (!hasNoBundle && item.driftStatus === "drifted") {
		reasons.push({
			id: "drifted",
			label: "Drifted",
			issue: "Drifted from IaC",
			why: "The live bundle no longer matches its declared configuration.",
			level: "attention",
		});
	}
	if (!hasNoBundle && item.driftStatus === "error") {
		reasons.push({
			id: "drift-check-failed",
			label: "Drift check failed",
			issue: "Drift check failed",
			why: "The nightly check could not plan this bundle, so its state is unknown.",
			level: "attention",
		});
	}
	// Teardown keeps the warning columns, but there is nothing left to reclaim.
	if (!hasNoBundle && item.lifecycle === "expiring") {
		const held = isHoldActive(item.holdUntil, now);
		const until = held ? item.holdUntil : item.noticeDeadline;
		const date = until ? ` on ${dayMonth(until)}` : "";
		reasons.push({
			id: "expiring",
			label: "Expiring",
			issue: held
				? `Expiring — held until ${until ? dayMonth(until) : "further notice"}`
				: `Expiring — reclaimed${date}`,
			why: held
				? "A warning was recorded, but an admin hold blocks reclamation."
				: "A warning was recorded; it is reclaimed on the date it named unless it is used or held.",
			level: "attention",
		});
	}
	return reasons;
}

/**
 * A bundle's health: failed, attention or healthy, with every reason, most severe first. A type's own
 * reasons come from its descriptor; being behind main is a tile of its own, not a reason.
 */
export function deriveHealth(
	item: BundleInventoryItem,
	descriptor: {
		healthReasons?: (item: BundleInventoryItem) => HealthReason[];
		hasNoBundle?: (item: BundleInventoryItem) => boolean;
	},
	now: Date = new Date(),
): BundleHealth {
	const reasons = [
		...(descriptor.healthReasons?.(item) ?? []),
		...genericReasons(item, now, descriptor.hasNoBundle?.(item) === true),
	].sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level]);
	const worst = reasons.find((r) => r.level !== "info")?.level;
	return { level: worst && worst !== "info" ? worst : "healthy", reasons };
}

/** Sort order of the Health column: failed, then attention, then healthy. */
export const healthRank = (health: BundleHealth): number => LEVEL_RANK[health.level];
